import React from 'react';
import { useHistory } from 'react-router-dom';
import { FiPlus, FiCheck } from 'react-icons/fi';
import { FcGoogle } from 'react-icons/fc';
import { FaLinkedin } from 'react-icons/fa';
import { toast } from '../../../Toast';
import { WidgetDef, SettingValues, GENERATE_NOTE_SINGLE } from '../types';
import { DemoHead, useWidgetSite } from '../shared';

// the description default names the client, so it can't be a static text default: unset = this
const useDescription = (s: SettingValues) => {
    const { short } = useWidgetSite();
    return s.description === undefined ? `Join the ${short} list and get announcements the moment they are released` : String(s.description);
};

const DescriptionField: React.FC<{ settings: SettingValues; set: (key: string, value: string | boolean) => void }> = ({ settings, set }) => {
    const description = useDescription(settings);
    return <label className="wd-text">
        <span>Description</span>
        <textarea rows={2} value={description} onChange={e => set('description', e.target.value)} />
    </label>;
};

const WelcomeEmail: React.FC = () => {
    const history = useHistory();
    const { short } = useWidgetSite();
    return <div className="wd-welcome">
        <p>Everyone who signs up here gets the Welcome email, with their password and a link to manage what they receive.</p>
        <div className="assigned"><span className="pill">Assigned</span><strong>Welcome email — {short}</strong></div>
        <button type="button" className="btn-outline" onClick={() => history.push('/communications')}>Edit the Welcome Email →</button>
        <small>Opens Communications › Email templates</small>
    </div>;
};

// Google / LinkedIn buttons under an "or … with" divider; null when social is off or both are off
export const SocialRow: React.FC<{ s: SettingValues; divider: string }> = ({ s, divider }) =>
    s.social && (s.google || s.linkedin)
        ? <>
            <div className="wd-divider">{divider}</div>
            <div className="wd-social">
                {s.google && <button type="button" onClick={() => toast.info('Preview only: Google sign-in is not connected here')}><FcGoogle /> Google</button>}
                {s.linkedin && <button type="button" onClick={() => toast.info('Preview only: LinkedIn sign-in is not connected here')}><FaLinkedin className="linkedin" /> LinkedIn</button>}
            </div>
        </>
        : null;

// spec 623:5574
const signUp: WidgetDef = {
    slug: 'sign-up',
    name: 'Sign-up and announcements',
    description: 'Investors join the list and choose what they receive',
    icon: <FiPlus />,
    title: 'Sign-Up and Announcements Widget',
    installed: true,
    page: '/investors/investors-dashboard/',
    views: 1240,
    demoPath: '/investors/investors-dashboard/',
    addNote: 'This code is generated from the settings below. Change a setting and the code updates — paste it once, it stays current.',
    addBlocks: [
        { title: '1 · WordPress shortcode', code: '[investi_add_subscriber instance="signup"]' },
        { title: '2 · Or plain HTML', code: '<div data-investi-instance="signup" class="investi-add-subscriber"\n     data-investi-email-alerts-options\n     data-investi-is-shareholder-option></div>', dark: true },
        {
            title: '3 · Optional attributes',
            code: 'data-investi-submit-button-text="Submit"\ndata-investi-email-alerts-heading="Email Alerts"\ndata-investi-is-shareholder-text="Do you hold shares?"\ndata-investi-google-subscribe\ndata-investi-linkedin-subscribe',
            dark: true,
            after: 'Leave an attribute out and that part of the form does not appear.',
        },
    ],
    settings: [
        { kind: 'heading', label: 'Title and text' },
        { kind: 'text', key: 'title', label: 'Title', default: 'Subscribe for updates' },
        { kind: 'custom', render: DescriptionField },
        { kind: 'text', key: 'button', label: 'Button label', default: 'Submit' },
        { kind: 'heading', label: 'Signing up' },
        { kind: 'toggle', key: 'social', label: 'Sign up with social media', hint: 'Shows the “or sign up with” row', default: true },
        { kind: 'toggle', key: 'google', label: 'Sign up with Google', default: true },
        { kind: 'toggle', key: 'linkedin', label: 'Sign up with LinkedIn', default: true },
        { kind: 'heading', label: 'Emails' },
        { kind: 'custom', render: WelcomeEmail },
    ],
    generateNote: GENERATE_NOTE_SINGLE,
    Demo: ({ settings: s }) => {
        const description = useDescription(s);
        return <>
            <DemoHead title={String(s.title)} sub={description} />
            <form className="wd-form" onSubmit={e => { e.preventDefault(); toast.info('Preview only: nobody was signed up'); }}>
                <div className="row">
                    <input className="wd-input" placeholder="First name" aria-label="First name" />
                    <input className="wd-input" placeholder="Last name" aria-label="Last name" />
                </div>
                <input className="wd-input" type="email" placeholder="Email address" aria-label="Email address" />
                <h4>Email Alerts</h4>
                <div className="options">
                    <label className="opt"><input type="checkbox" defaultChecked /> ASX Announcements</label>
                    <label className="opt"><input type="checkbox" defaultChecked /> Media Releases and other news</label>
                </div>
                <h4>Do you currently hold shares in this Company?</h4>
                <div className="options inline">
                    <label className="opt"><input type="radio" name="wd-shareholder" defaultChecked /> Yes</label>
                    <label className="opt"><input type="radio" name="wd-shareholder" /> No</label>
                </div>
                <button type="submit" className="wd-btn-navy">{String(s.button)}</button>
                <SocialRow s={s} divider="or sign up with" />
                <p className="success"><FiCheck /> Thanks — check your email. We have sent you a password so you can sign in and manage what you receive.</p>
            </form>
        </>;
    },
};

export default signUp;
